// fireEventOnce.js -- Fire-and-forget funnel event utility for WLC referral tracking.
//
// Two entry points:
//
//   fireFunnelEvent({ eventName, source, stepId?, phase?, stepIndex?, once })
//     Unified API for all funnel events. When once=true, dedupes via either a
//     legacy single-flag storageKey (milestones) or the STEP_EVENTS_LOGGED JSON
//     bag (per-step events keyed as `${eventName}:${stepId}`). When once=false,
//     every call hits the network (used for submission_attempt / submission_failed
//     so retries remain visible).
//
//   fireEventOnce(eventName, source, storageKey)
//     Thin wrapper around fireFunnelEvent for the original milestone events
//     (form_start, screening_complete, etc.). Kept so existing call sites and
//     tests keep working with zero behaviour change.
//
// Design decisions:
// - Standalone module (not a React hook) so it's testable without rendering.
// - Dedup is synchronous (localStorage check-then-set before the network call)
//   so React Strict Mode double-effects and rapid re-renders cannot double-write.
//   Trade-off: if the network call fails, the event is permanently dropped for
//   this attempt — same as the original milestone design; we prefer no dupes
//   over perfect capture.
// - Fire-and-forget with swallowed errors — analytics must never block the form.
// - Never send answer values / PII — only stepId, phase, stepIndex metadata.
// - Every milestone is additionally mirrored into the canonical analytics event
//   stream (src/analytics/screenerEvents.js) from this one place, so the two
//   destinations cannot drift and no call site needs to know about the second
//   one. The Firestore write is unchanged and always happens first.

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { mirrorScreenerFunnelEvent, SCREENER_RTC_ASRS6 } from '../../../analytics/screenerEvents';

const ORGANISATION = 'Well Life Clinic';

/**
 * Returns the current session UUID, creating one if it doesn't exist yet.
 * The sessionId ties all funnel events from a single form attempt together,
 * enabling per-user journey analysis in Firestore.
 */
export function getOrCreateSessionId() {
    let sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    if (!sessionId) {
        sessionId = crypto.randomUUID();
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
    }
    return sessionId;
}

/**
 * Read the step-event dedup bag from localStorage.
 * Corrupt / missing JSON is treated as an empty bag so a bad write never
 * permanently blocks future tracking for the session.
 */
function readStepEventsBag() {
    const raw = localStorage.getItem(STORAGE_KEYS.STEP_EVENTS_LOGGED);
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
}

function writeStepEventsBag(bag) {
    localStorage.setItem(STORAGE_KEYS.STEP_EVENTS_LOGGED, JSON.stringify(bag));
}

/**
 * Fire a funnel tracking event to logReferralFormEvent.
 *
 * @param {object} opts
 * @param {string} opts.eventName - Allowed event name (e.g. 'step_viewed', 'form_start').
 * @param {string} opts.source - Route source identifier (e.g. 'main').
 * @param {string} [opts.stepId] - Canonical step id for step_* events.
 * @param {string} [opts.phase] - Funnel phase label (non-PII).
 * @param {number} [opts.stepIndex] - Optional numeric index within the phase / question list.
 * @param {boolean} [opts.once=true] - When true, dedupe; when false, always fire.
 * @param {string} [opts.storageKey] - Legacy single-flag key for milestone once-events.
 *   When omitted and once=true with a stepId, uses the STEP_EVENTS_LOGGED bag.
 * @param {boolean} [opts.skipped] - Analytics only: an optional question advanced
 *   with no answer given. Never written to Firestore.
 * @param {object} [opts.scoring] - Analytics only: {partAScore, partAMax, threshold,
 *   meetsThreshold} for the screening-complete milestone. Never written to Firestore.
 */
export function fireFunnelEvent({
    eventName,
    source,
    stepId,
    phase,
    stepIndex,
    once = true,
    storageKey,
    skipped,
    scoring,
} = {}) {
    // Entire body is try/caught: analytics must never throw into Next / Submit /
    // welcome handlers (localStorage quota, missing crypto, callable init, etc.).
    try {
        if (!eventName || !source) return;

        if (once) {
            if (storageKey) {
                // Milestone path: one dedicated localStorage flag per event type.
                if (localStorage.getItem(storageKey)) return;
                localStorage.setItem(storageKey, 'true');
            } else if (stepId) {
                // Per-step path: bag keyed by eventName:stepId.
                const bagKey = `${eventName}:${stepId}`;
                const bag = readStepEventsBag();
                if (bag[bagKey]) return;
                bag[bagKey] = true;
                writeStepEventsBag(bag);
            } else {
                // once=true with neither storageKey nor stepId would be a silent no-op
                // forever after the first call if we invented a key — refuse instead.
                return;
            }
        }

        const sessionId = getOrCreateSessionId();
        const payload = {
            organisation: ORGANISATION,
            eventName,
            source,
            sessionId,
        };
        if (stepId) payload.stepId = stepId;
        if (phase) payload.phase = phase;
        if (typeof stepIndex === 'number' && Number.isFinite(stepIndex)) {
            payload.stepIndex = stepIndex;
        }

        const logFn = httpsCallable(functions, 'logReferralFormEvent');
        logFn(payload).catch(() => {});

        // Mirror the same milestone into the canonical analytics event stream.
        // This is the one choke point every funnel event already passes through,
        // so bridging here covers every existing and future milestone without
        // touching a call site, and inherits the dedup decided above — the
        // mirror is only reached when the Firestore write is.
        //
        // An addition, never a replacement: `payload` above is unchanged, and
        // the mirror runs in its own try/catch so nothing about the warehouse
        // can affect this form. The mirror inherits the DEDUP decision, not the
        // write's outcome - the callable above is fire-and-forget, so a failed
        // funnel write can still produce a mirrored row. Both sinks are
        // analytics, so nothing false is claimed about product state; the two
        // funnels can differ by exactly the failed-write rows.
        try {
            mirrorScreenerFunnelEvent({
                eventName,
                screenerKey: SCREENER_RTC_ASRS6,
                orgName: ORGANISATION,
                source,
                sessionId,
                stepId,
                phase,
                stepIndex,
                skipped,
                scoring,
            });
        } catch (_) {
            // Swallow — analytics must never break a public referral form.
        }
    } catch (_) {
        // Swallow — form UX must continue even if tracking is unavailable.
    }
}

/**
 * Fire a funnel tracking event exactly once per form attempt (milestone helper).
 *
 * @param {string} eventName - One of the allowed event names (e.g. 'form_start').
 * @param {string} source - The route source identifier (e.g. 'main', 'nhs-adhd-2').
 * @param {string} storageKey - The localStorage key used as a dedup flag for this event.
 */
export function fireEventOnce(eventName, source, storageKey) {
    fireFunnelEvent({ eventName, source, once: true, storageKey });
}
